<script setup lang="js">
import Lucide from "@/base-components/Lucide";
import Button from "@/base-components/Button";
import Tippy from "@/base-components/Tippy";
import {useFetch} from "@/composables/useFetch.js";
import {useRouter} from "vue-router";
import {uri} from "@/constants/config.js";
import {ref} from "vue";
import moment from "moment-jalaali";
import TicketTable from "@/pages/ticket/TicketTable.vue";

moment.loadPersian({usePersianDigits: true});
const router = useRouter()

const iaasCount = ref(0)
const ticketCount = ref(0)

async function getIaasCount() {
  const {data} = await useFetch(uri.api.iaas.instance.list)
  iaasCount.value = data.meta.total
}

getIaasCount();

const isLoadingNotifications = ref(true)
const notifications = ref([])
const notificationsMeta = ref({})

async function getNotifications() {
  isLoadingNotifications.value = true
  const {data} = await useFetch(uri.api.notification.list)
  notifications.value = data.data
  notificationsMeta.value = data.meta
  isLoadingNotifications.value = false
}

getNotifications()


const isLoadingTickets = ref(true)
const tickets = ref([])
const ticketsMeta = ref({})

async function getTickets() {
  isLoadingTickets.value = true
  const {data} = await useFetch(uri.api.ticket.preview)
  tickets.value = data.data
  ticketsMeta.value = data.meta
  ticketCount.value = data.meta.total
  isLoadingTickets.value = false
}

function gotoTicket(ticket) {
  router.push(`/ticket/${ticket.id}`)
}

getTickets()

</script>

<template>
  <div class="grid grid-cols-12 gap-6 mt-5">
    <div class="col-span-12 sm:col-span-6 xl:col-span-3 intro-y">
      <div
          :class="[
                  'relative zoom-in',
                  'before:content-[\'\'] before:w-[90%] before:shadow-[0px_3px_5px_#0000000b] before:bg-white/60 before:h-full before:mt-2.5 before:absolute before:rounded-md before:mx-auto before:inset-x-0 before:dark:bg-darkmode-400/70',
                ]"
      >
        <div class="p-5 box">
          <RouterLink :to="{name: 'side-menu-list-vm-server'}">
            <div class="flex">
              <Lucide
                  icon="Monitor"
                  class="w-[28px] h-[28px] text-warning"
              />
              <div class="mr-auto">
                <Tippy
                    as="div"
                    class="cursor-pointer bg-success py-[3px] flex rounded-full text-white text-xs pr-2 pl-1 items-center font-medium"
                    content=""
                >
                  {{ $t('button.view') }}
                  <Lucide icon="ChevronLeft" class="w-4 h-4 mr-0.5"/>
                </Tippy>
              </div>
            </div>
            <div class="mt-6 text-3xl font-medium leading-8">{{ iaasCount }}</div>
            <div class="mt-1 text-base text-slate-500">
              {{ $t('title.iaas') }}
            </div>
          </RouterLink>
        </div>
      </div>
    </div>
    <div class="col-span-12 sm:col-span-6 xl:col-span-3 intro-y">
      <div
          :class="[
                  'relative zoom-in',
                  'before:content-[\'\'] before:w-[90%] before:shadow-[0px_3px_5px_#0000000b] before:bg-white/60 before:h-full before:mt-2.5 before:absolute before:rounded-md before:mx-auto before:inset-x-0 before:dark:bg-darkmode-400/70',
                ]"
      >
        <div class="p-5 box">
          <RouterLink :to="{name: 'ticket-list'}">

            <div class="flex">
              <Lucide
                  icon="Mail"
                  class="w-[28px] h-[28px] text-primary"
              />
              <div class="mr-auto">
                <Tippy
                    as="div"
                    class="cursor-pointer bg-success py-[3px] flex rounded-full text-white text-xs pr-2 pl-1 items-center font-medium"
                    content="نمایش تیکت ها"
                >
                  {{ $t('button.view') }}
                  <Lucide icon="ChevronLeft" class="w-4 h-4 mr-0.5"/>
                </Tippy>
              </div>
            </div>
            <div class="mt-6 text-3xl font-medium leading-8">{{ ticketCount }}</div>
            <div class="mt-1 text-base text-slate-500">{{ $t('menu.ticket') }}</div>
          </RouterLink>
        </div>
      </div>
    </div>
  </div>

  <div class="mt-5 intro-y grid grid-cols-12 gap-6">
    <div class="col-span-12">
      <div
          class="relative before:content-[''] before:w-[95%] before:shadow-[0px_3px_5px_#0000000b] before:bg-white/60 before:h-full before:mt-2.5 before:absolute before:rounded-md before:mx-auto before:inset-x-0 before:dark:bg-darkmode-400/70"
      >
        <div class="box">
          <div
              class="flex flex-col justify-center col-span-12 px-8 py-12 lg:col-span-6"
          >
            <Lucide icon="Newspaper" class="w-10 h-10 text-pending"/>
            اطلاع رسانی
            <div
                class="mt-5 relative">
              <free-style-shimmer v-if="isLoadingNotifications" v-for="() in [1,2,3]" height="40px" width="100%"
                                  class="mb-3"/>
              <div v-for="notification in notifications" v-if="!isLoading"
                   class="relative flex items-center mb-3 intro-x">
                <div class="flex-1 px-5 py-3 ml-4 box zoom-in">
                  <div class="text-slate-500">
                    {{ notification.message }}
                    <div v-if="notification.link" class="flex mt-2">
                      <Button class="flex items-center !box text-slate-600 dark:text-slate-300 p-3">
                        <Lucide icon="FileText" class="hidden w-4 h-4 mr-2 sm:block"/>
                        باز کردن
                      </Button>
                    </div>
                  </div>
                  <div class="flex items-center">
                    <div class="mr-auto text-xs text-slate-500">
                      {{ moment(notification.createdAt).locale("fa").format("jYYYY/jMM/jDD HH:mm:ss") }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
              class="col-span-12 p-8 border-t border-dashed lg:col-span-8 lg:border-t-0 lg:border-l border-slate-200 dark:border-darkmode-300"
          >
          </div>
        </div>
      </div>
    </div>

    <div class="col-span-12">
      <div
          class="relative before:content-[''] before:w-[95%] before:shadow-[0px_3px_5px_#0000000b] before:bg-white/60 before:h-full before:mt-2.5 before:absolute before:rounded-md before:mx-auto before:inset-x-0 before:dark:bg-darkmode-400/70"
      >
        <div class="box">
          <div
              class="flex flex-col justify-center col-span-12 px-8 py-12 lg:col-span-6"
          >
            <Lucide icon="TicketCheck" class="w-10 h-10 text-success"/>
            آخرین تیکت ها
            <div
                class="mt-5 relative">
              <free-style-shimmer v-if="isLoadingTickets" v-for="() in [1,2,3]" height="40px" width="100%"
                                  class="mb-3"/>
              <TicketTable :is-loading="isLoadingTickets" :tickets="tickets" />

            </div>


          </div>
          <div
              class="col-span-12 p-8 border-t border-dashed lg:col-span-8 lg:border-t-0 lg:border-l border-slate-200 dark:border-darkmode-300"
          >

          </div>
        </div>
      </div>
    </div>
  </div>
</template>
